<template>
    <component
        v-show="!!$slots.default"
        :is="inline ? 'span' : 'div'"
        class="notification"
        :class="{
            [`btcdirect-${type}`] : !!type,
            'notification--with-icon': icons[type],
        }"
    >
        <template v-if="!inline && !!type">
            <component :is="icons[type]" solid />
            <span>
                <slot />
            </span>

            <template v-if="$slots['footer']">
                <div class="notification__footer">
                    <slot name="footer" />
                </div>
            </template>
        </template>

        <template v-else>
            <slot />

            <template v-if="$slots['footer']">
                <div class="notification__footer">
                    <slot name="footer" />
                </div>
            </template>
        </template>

        <template v-if="type === 'private-trading'">
            <svg class="notification__private-trading-shape notification__private-trading-shape--top" width="433" height="433" viewBox="0 0 433 433" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M424.657 160.724C455.445 275.627 387.178 393.869 272.275 424.657C157.373 455.445 39.1309 387.178 8.34279 272.275C-22.4453 157.373 45.8217 39.131 160.724 8.34301C275.627 -22.445 393.869 45.822 424.657 160.724ZM265.974 203.243C258.638 175.865 230.62 159.69 203.242 167.026C175.864 174.362 159.689 202.379 167.024 229.757C174.36 257.135 202.378 273.311 229.756 265.976C257.134 258.64 273.31 230.621 265.974 203.243Z" fill="url(#paint0_linear_0_4)"/>
                <defs>
                <linearGradient id="paint0_linear_0_4" x1="236.448" y1="426.959" x2="128.546" y2="24.263" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#0086FF"/>
                    <stop offset="1" stop-color="#034FA0"/>
                </linearGradient>
                </defs>
            </svg>

            <svg class="notification__private-trading-shape notification__private-trading-shape--bottom" width="232" height="232" viewBox="0 0 232 232" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M145.417 3.95923C207.001 20.4605 243.59 83.8337 227.088 145.418C210.587 207.001 147.213 243.59 85.6298 227.088C24.0463 210.587 -12.5425 147.214 3.9595 85.63C20.4603 24.0465 83.8335 -12.5421 145.417 3.95923ZM122.628 89.0078C107.955 85.0761 92.9382 93.7458 89.0064 108.42C85.0748 123.093 93.7445 138.11 108.418 142.042C123.092 145.973 138.108 137.304 142.04 122.63C145.972 107.957 137.302 92.9397 122.628 89.0078Z" fill="url(#paint0_linear_1_5)"/>
                <defs>
                <linearGradient id="paint0_linear_1_5" x1="218.556" y1="162.664" x2="2.72551" y2="104.833" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#0086FF"/>
                    <stop offset="1" stop-color="#034FA0"/>
                </linearGradient>
                </defs>
            </svg>
        </template>
    </component>
</template>

<script lang="ts" setup>
import CheckIcon from '../icons/check.vue';
import DangerIcon from '../icons/danger.vue';
import InfoIcon from '../icons/info.vue';
import WarningIcon from '../icons/warning.vue';

defineProps({
    type: {
        type: String as () => 'danger' | 'info' | 'success' | 'warning' | 'private-trading',
        required: false,
        default: null
    },
    inline: {
        type: Boolean,
        required: false,
        default: false
    }
});

const icons = {
    danger: DangerIcon,
    info: InfoIcon,
    success: CheckIcon,
    warning: WarningIcon
};
</script>

<style lang="scss" scoped>
@use '@@scss/flex';
@use '@@scss/transitions';

.notification {
    --icon-size: 1.75em;

    display: grid;
    color: var(--notification-color);
    column-gap: 0.5em;
    line-height: 1.75em;

    &.notification--with-icon {
        grid-template-columns: var(--icon-size) 1fr;
    }

    & .notification__footer {
        margin-top: 10px;
        grid-column: 1 / -1;
        display: grid;
        gap: 8px;
    }

    svg:not([class*='notification__private-trading-shape']) {
        color: var(--btcdirect-blue);
        flex-shrink: 0;
        height: 1.5em;
        width: 1.5em;
    }

    &.btcdirect-info,
    &.btcdirect-danger,
    &.btcdirect-success,
    &.btcdirect-warning,
    &.btcdirect-private-trading {
        border-radius: 0.5rem;
        padding: 0.75rem;
    }

    &.btcdirect-info {
        background-color: var(--notification-info-background-color);

        svg:not([class*='notification__private-trading-shape']) {
            height: var(--icon-size);
            width: var(--icon-size);
        }
    }

    &.btcdirect-danger {
        background-color: var(--notification-danger-background-color);
    }

    &.btcdirect-success {
        background-color: var(--notification-success-background-color);
    }

    &.btcdirect-warning {
        background-color: var(--notification-warning-background-color);
    }

    &.btcdirect-private-trading {
        background-color: var(--notification-private-trading-background-color);
        color: var(--notification-private-trading-color);
        position: relative;
        overflow: hidden;

        > *:not([class*='notification__private-trading-shape']) {
            position: relative;
            z-index: 1;
        }
    }

    &.btcdirect-private-trading .notification__private-trading-shape {
        position: absolute;
        opacity: 0.65;

        &--top {
            top: 0;
            right: 0;
            transform: translate(50%, -50%) rotate(-20deg);
            max-width: 90%;
        }

        &--bottom {
            bottom: 0;
            left: 0;
            transform: translate(-50%, 50%) rotate(-15deg);
            max-width: 50%;
        }
    }
}
</style>
